import { Reveal, SectionHead, GoldBtn, PageHero, StatCell, Particles } from "@/components/Shared";
import { STATS, PILLARS, EVENT } from "@/data/content";
import { Compass, Eye, HeartHandshake, Landmark } from "lucide-react";

const BELIEFS = [
    { icon: Eye, title: "Ideas are celebrated", text: "Here, ideas are not showcased — they're celebrated. Every venture gets a stage, a story and a home." },
    { icon: HeartHandshake, title: "Startups collaborate", text: "Here, startups don't compete — they collaborate. The bazaar exists so the ecosystem can rise together." },
    { icon: Compass, title: "The future is felt", text: "Because innovation is not just built — it's felt. MahaBazaar is designed to be experienced, not attended." },
    { icon: Landmark, title: "Building Bharat's identity", text: "A platform for the next generation of Bharat — and for the India of 2047." },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <PageHero
                testid="about-hero"
                eyebrow="About the Festival"
                title={<>India's Grand Festival of <span className="gold-text">Startups, Innovation &amp; Entrepreneurship</span></>}
                sub="Startup MahaBazaar 2027 is the Bazaar of the Future — where every idea finds its market, every innovator finds a mentor, and every startup finds its story."
            >
                <GoldBtn to="/register" testid="about-register-btn">Register Now</GoldBtn>
                <GoldBtn to="/experience" variant="outline" testid="about-experience-btn">Explore the Experience</GoldBtn>
            </PageHero>

            <section className="py-24 md:py-32 noise relative overflow-hidden">
                <Particles density={20} />
                <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
                    <Reveal>
                        <span className="eyebrow no-lines">The Philosophy</span>
                        <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.06] mt-6">
                            One Bazaar of the Future. <span className="gold-text italic">An Emotion.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={140}>
                        <p className="text-muted-luxe text-base md:text-lg leading-relaxed">
                            Startup MahaBazaar is India's festival of fearless imagination — an "Idea Street" where
                            innovation has a face, a voice and a heartbeat. It is where India's entrepreneurial
                            heartbeat becomes a festival for the world to witness.
                        </p>
                        <p className="mt-6 text-muted-luxe text-base md:text-lg leading-relaxed">
                            Over three days in New Delhi, {EVENT.venue.split(",")[0]} transforms into a futuristic
                            marketplace of golden arches and glowing pavilions — a national innovation experience
                            for the startups of Viksit Bharat.
                        </p>
                        <div className="mt-9 flex flex-wrap gap-3">
                            {PILLARS.map((p) => (
                                <span key={p} className="px-4 py-2 border hairline text-[0.6rem] tracking-[0.32em] uppercase text-[var(--champagne)]/85">{p}</span>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C060C]/60">
                <div className="container-x">
                    <SectionHead eyebrow="What We Believe" title={<>The Spirit of <span className="gold-text">the Bazaar</span></>} />
                    <div className="mt-16 grid sm:grid-cols-2 gap-5">
                        {BELIEFS.map((b, i) => (
                            <Reveal key={b.title} delay={i * 80}>
                                <div className="card-luxe tilt p-8 h-full">
                                    <div className="card-glow" />
                                    <b.icon size={24} strokeWidth={1.2} className="text-[var(--gold)]" />
                                    <h3 className="font-display text-2xl mt-6 text-[var(--ivory)]">{b.title}</h3>
                                    <p className="mt-3 text-sm text-muted-luxe leading-relaxed">{b.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 border-y hairline">
                <div className="container-x grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-[rgba(201,162,75,0.08)]">
                    {STATS.map((s) => (
                        <StatCell key={s.label} value={s.value} suffix={s.suffix} label={s.label} />
                    ))}
                </div>
            </section>

            <section className="py-24 md:py-32">
                <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
                    <Reveal>
                        <img src="/logo.png" alt="Startup MahaBazaar 2027 lockup" className="w-56 md:w-72 mx-auto floaty" loading="lazy" />
                    </Reveal>
                    <Reveal delay={140}>
                        <span className="eyebrow no-lines">The Organiser</span>
                        <h3 className="font-display text-3xl mt-5 text-[var(--ivory)]">Presented by {EVENT.organiser}</h3>
                        <p className="mt-5 text-muted-luxe leading-relaxed">
                            Startup MahaBazaar 2027 is an initiative by {EVENT.organiser} — building Bharat's
                            innovation identity through a festival where ideas meet capital, markets, technology
                            and impact.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <GoldBtn to="/partner" variant="outline" testid="about-partner-btn">Partner With Us</GoldBtn>
                            <GoldBtn to="/contact" variant="dark" testid="about-contact-btn">Contact the Team</GoldBtn>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
